<template>
  <scoped>
    <VerticalNavigator>
      <Breadcrumb
        v-if="customer"
        :links="[{
          title: 'RutSolve',
          routeName: 'index'
        }, {
          title: 'Customers',
          routeName: 'customers'
        }]"
        :title="customer.name"
      />
      <h1>
        Edit Customer
      </h1>
      <CustomerEditor v-model:value="customer" />
      <h2>
        Contact Details
      </h2>
      <ContactDetailsList
        v-if="customer && customer.contactDetails"
        v-model:items="customer.contactDetails.records"
        v-model:selectedIndex="selectedContactDetailsIndex"
        @select="selectedContactDetails = customer.contactDetails.records[selectedContactDetailsIndex]"
      />
      <ContentModal v-if="selectedContactDetails">
        <ContactDetailsEditor v-model:value="selectedContactDetails" />
        <br>
        <Button
          title="Apply"
          @click="selectedContactDetails = null"
        />
      </ContentModal>
      <Button
        title="Remove"
        @click="confirmRemove = true"
      />
      <Button
        title="Cancel"
        @click="customersRedirect()"
      />
      <Button
        title="Save"
        @click="save()"
      />
    </VerticalNavigator>
    <ConfirmationModal
      v-model:display="confirmRemove"
      :callback="remove"
      message="This action will remove selected customer."
      title="Remove Customer"
    />
    <ErrorModal
      v-if="error"
      v-model:error="error"
    />
  </scoped>
</template>

<script setup lang="ts">
  import Button from "@/components/button.vue"
  import CustomerEditor from "@/components/editors/customer.vue"
  import VerticalNavigator from "@/layouts/vertical_navigator.vue"
  import Breadcrumb from "@/components/breadcrumb.vue"
  import {useRoute} from "vue-router"
  import {Customer as CustomerClient} from "@/endpoints/rutsolve/gql/resolvers/customer"
  import {ContactDetails as ContactDetailsClient} from "@/endpoints/rutsolve/gql/resolvers/contact_details"
  import {onBeforeMount, ref} from "vue"
  import {ContactDetails, Customer} from "@/endpoints/rutsolve/gql/generated/graphql"
  import ErrorModal from "@/components/modals/error.vue"
  import ConfirmationModal from "@/components/modals/confirmation.vue"
  import router from "@/router"
  import ContactDetailsList from "@/components/lists/contact_details.vue"
  import ContactDetailsEditor from "@/components/editors/contact_details.vue"
  import ContentModal from "@/components/modals/content.vue"

  const error = ref<Error>(null)
  const contactDetailsClient = new ContactDetailsClient()
  const customerClient = new CustomerClient()
  const customer = ref<Customer>(null)
  const confirmRemove = ref<boolean>(false)
  const selectedContactDetails = ref<ContactDetails>(null)
  const selectedContactDetailsIndex = ref<number>(null)

  onBeforeMount(() => {
    read()
  })

  async function read() {
    try {
      const customerID = useRoute().params.customerID.toString()
      customer.value = await customerClient.summary(customerID)
      if (!customer.value.contactDetails || !customer.value.contactDetails.records) {
        customer.value.contactDetails = {
          records: []
        }
      }
    } catch(e) {
      error.value = e
    }
  }

  async function remove() {
    try {
      await customerClient.remove(customer.value.id)
      customersRedirect()
    } catch(e) {
      error.value = e
    }
  }

  async function save() {
    try {
      await customerClient.update(customer.value)
      for (const contactDetails of customer.value.contactDetails.records) {
        contactDetails.relationID = customer.value.id
        contactDetails.relationType = "CUSTOMER"
        if (contactDetails.deletedAt) {
          await contactDetailsClient.remove(contactDetails.id)
        } else if (contactDetails.id) {
          await contactDetailsClient.update(contactDetails)
        } else {
          await contactDetailsClient.create(contactDetails)
        }
      }
      customersRedirect()
    } catch(e) {
      error.value = e
    }
  }
  
  function customersRedirect() {
    router.push({
      name: "customers"
    })
  }
</script>
